import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../api";
import { PageHeading } from "../externalComponents";
import { calculateProgress } from "../externalComponents/onboardingPages/calculateProgress";
import { CollectOptins } from "../externalComponents/onboardingPages/CollectOptins";
import {
  AddWhatsAppPaymentMethod,
  ChoosePlan,
  CompleteWhatsAppProfile,
  CreateWhatsAppAccount,
  NotifyCustomerService,
  SendReceiveMessages,
  SychronizeContacts,
} from "../externalComponents/onboardingPages/index";
import onboardingSteps from "../externalComponents/onboardingPages/onboardingSteps";
import { QTrack } from "../externalComponents/onboardingPages/QTrack";
import { SetupSidebar } from "../externalComponents/onboardingPages/setupSidebar";
import { Button } from "../externalComponents/tailwind/button";
import { CustomerContext } from "../providers";

export default function Onboarding() {
  const OnboardingComponents = {
    0: ChoosePlan,
    1: CreateWhatsAppAccount,
    2: AddWhatsAppPaymentMethod,
    3: CompleteWhatsAppProfile,
    4: SendReceiveMessages,
    5: SychronizeContacts,
    8: QTrack,
    6: NotifyCustomerService,
    7: CollectOptins,
  };

  const navigate = useNavigate();
  const { customer } = useContext(CustomerContext);

  const [customerSteps, setCustomerSteps] = useState(
    customer?.onboardingProgress || {},
  );
  const [activeSteps, setActiveSteps] = useState([]);
  const [nextStep, setNextStep] = useState(Object.keys(onboardingSteps)[0]);
  const [currentStep, setCurrentStep] = useState(null);
  const [progression, setProgression] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const firstInvalidateStep = Object.entries(customerSteps).find(
    ([, step]) => step.validate === false,
  );

  if (firstInvalidateStep) {
    () => setNextStep(firstInvalidateStep);
    console.log(`The first step that is not visible is Step ${nextStep}:`);
  } else {
    console.log("All steps are visible.");
  }

  useEffect(() => {
    if (customer && customer.onboardingProgress) {
      const active = Object.keys(onboardingSteps).filter(
        (key) => onboardingSteps[key].visible === true,
      );
      setActiveSteps(active);
      try {
        setCustomerSteps(JSON.parse(customer?.onboardingProgress));
      } catch (e) {
        console.log(e);
      }
    }
  }, [customer]);

  useEffect(() => {
    if (activeSteps.length > 0 && Object.keys(customerSteps).length > 0) {
      const updatedCustomerSteps = activeSteps.reduce(
        (acc, step) => {
          acc[step] = customerSteps[step]
            ? {
                name: onboardingSteps[step].name,
                validate: customerSteps[step].validate,
              }
            : {
                name: onboardingSteps[step].name,
                validate: false,
              };
          return acc;
        },
        { completed: customerSteps.completed },
      );

      Object.keys(customerSteps).forEach((step) => {
        if (step !== "completed" && !activeSteps.includes(step)) {
          delete customerSteps[step];
        }
      });

      setCustomerSteps(updatedCustomerSteps);
      setProgression(calculateProgress(updatedCustomerSteps));

      const firstUnvalidatedStep = activeSteps.find(
        (step) => !updatedCustomerSteps[step]?.validate,
      );
      const newNextStep = firstUnvalidatedStep || undefined;
      setNextStep(newNextStep);

      if (!currentStep) {
        setCurrentStep(newNextStep);
      }
    }
  }, [activeSteps]);

  const updateOnboardingProgress = async (step) => {
    setIsLoading(true);
    const updatedProgress = {
      ...customerSteps,
      [step]: { ...customerSteps[step], validate: true },
    };
    setCustomerSteps(updatedProgress);

    await api.customer.update(customer.id, {
      onboardingProgress: updatedProgress,
    });

    const activeSteps = Object.keys(updatedProgress);
    const nextStep =
      activeSteps.find((step) => !updatedProgress[step]?.validate) || undefined;
    setNextStep(nextStep);
    setCurrentStep(nextStep);
    // setTimeout(() => {
    // }, 1000);

    setIsLoading(false);
  };

  const handleCompleteSetup = async () => {
    const updatedProgress = {
      ...customerSteps,
      completed: true,
    };

    setCustomerSteps(updatedProgress);

    await api.customer.update(customer.id, {
      onboardingProgress: updatedProgress,
    });
    navigate("/dashboard");
  };

  const CurrentOnboardingComponent = OnboardingComponents[currentStep];

  return (
    <SetupSidebar
      customerSteps={customerSteps}
      currentStep={currentStep}
      nextStep={nextStep}
      progression={progression}
      onStepChange={setCurrentStep}
      onComplete={() => updateOnboardingProgress(currentStep)}
      setCustomerSteps={setCustomerSteps}
      handleCompleteSetup={handleCompleteSetup}
    >
      {currentStep && (
        <>
          {/* style de titre identique aux pages de setting */}
          <PageHeading title={customerSteps[currentStep]?.name} />
          {/* si on prend un style avec un titre encadré il faut que la hauteur de la div avec le overflow-y soit de (100vh-9em) */}
          {/* <Heading className="text-lg text-center mx-5 mt-5 p-5 border rounded-customBorderRadius sticky top-0">
            {customerSteps[currentStep]?.name}
          </Heading> */}
          <div className="overflow-y-auto max-lg:h-[calc(100vh-12em)] h-[calc(100vh-7em)] scrollbar mr-[-16px] mx-5 ">
            <div className="pr-[16px] flex flex-col justify-between h-full">
              <div className="my-5 pr-5">
                {CurrentOnboardingComponent ? (
                  <CurrentOnboardingComponent />
                ) : null}
              </div>
              <div className="flex justify-between pr-5">
                {currentStep !== "0" && (
                  <Button
                    color="primaryButton"
                    loading={isLoading}
                    disabled={
                      customerSteps[currentStep]
                        ? customerSteps[currentStep]?.validate
                        : true
                    }
                    onClick={() => {
                      updateOnboardingProgress(currentStep);
                    }}
                    className="text-center w-60 mx-auto mb-5"
                  >
                    Done
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </SetupSidebar>
  );
}
